import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useNotification } from '../contexts/NotificationContext';

type ReviewStatus = 'pending' | 'approved' | 'rejected';

interface CandidateQuestion {
  id: string;
  main_topic: string;
  subtopic: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: 'A' | 'B' | 'C' | 'D';
  difficulty: 'easy' | 'medium' | 'hard';
  difficulty_band: string;
  source: string;
  review_status: ReviewStatus;
  review_notes: string | null;
  created_at: string;
}

interface CandidateListResponse {
  success: boolean;
  data: {
    candidates: CandidateQuestion[];
  };
}

interface DuplicateMatch {
  questionId: string;
  questionText: string;
  category: string;
  difficulty: string;
  similarityScore: number;
  matchType: 'exact' | 'similar';
}

interface DuplicateDetectionResult {
  hasDuplicates: boolean;
  exactMatches: DuplicateMatch[];
  similarMatches: DuplicateMatch[];
  totalMatches: number;
}

const AdminQuestionReview: React.FC = () => {
  useDocumentTitle('Cognidaily | Review Candidate Questions');
  const { showSuccess, showError } = useNotification();

  const [candidates, setCandidates] = useState<CandidateQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [topicFilter, setTopicFilter] = useState<string>('');

  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject' | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [duplicateData, setDuplicateData] = useState<Record<string, DuplicateDetectionResult>>({});
  const [loadingDuplicates, setLoadingDuplicates] = useState<Set<string>>(new Set());
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mainTopicFromQuery = params.get('mainTopic');
    if (mainTopicFromQuery) {
      setTopicFilter(mainTopicFromQuery);
    }
  }, [location.search]);

  const loadCandidates = async (showRefreshAnimation = false) => {
    try {
      if (showRefreshAnimation) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const response = await axios.get<CandidateListResponse>(
        '/api/admin/candidate-questions',
        {
          params: {
            status: 'pending',
            mainTopic: topicFilter || undefined,
            excludeFailed: true // Exclude candidates from failed topic suggestions
          }
        }
      );
      if (response.data.success) {
        // Additional client-side filtering: exclude any candidates that might be from failed jobs
        // This is a safety measure in case the API doesn't filter properly
        const candidates = (response.data.data.candidates || []).filter((candidate: any) => {
          // If the API includes topic_suggestion_status, filter out failed ones
          if (candidate.topicSuggestionStatus === 'failed') {
            return false;
          }
          return true;
        });
        setCandidates(candidates);
      } else {
        setError('Failed to load candidate questions');
      }
    } catch (err: any) {
      console.error('[AdminQuestionReview] Failed to load candidates', err);
      setError(
        err?.response?.data?.message ||
          'Failed to load candidate questions from server'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicFilter]);

  // Check for duplicates when candidates are loaded - single batch request (up to 50)
  const DUPLICATE_CHECK_LIMIT = 50;
  useEffect(() => {
    const checkDuplicates = async () => {
      const pendingCandidates = candidates.filter(c => c.review_status === 'pending');
      const toCheck = pendingCandidates
        .slice(0, DUPLICATE_CHECK_LIMIT)
        .filter(c => !duplicateData[c.id]);

      if (toCheck.length === 0) return;

      const ids = toCheck.map((c) => c.id);
      setLoadingDuplicates((prev) => new Set([...prev, ...ids]));

      try {
        const response = await axios.post<{
          success: boolean;
          data: Record<string, DuplicateDetectionResult>;
        }>('/api/admin/candidate-questions/duplicates/batch', { ids });

        if (response.data.success && response.data.data) {
          setDuplicateData((prev) => ({ ...prev, ...response.data.data }));
        }
      } catch (err) {
        console.error('[AdminQuestionReview] Failed to check duplicates', err);
      } finally {
        setLoadingDuplicates((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      }
    };

    if (candidates.length > 0) {
      checkDuplicates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [topicFilter]);

  // Prune selection when new data loads (e.g. after approve/reject)
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const allowed = new Set(candidates.map((c) => c.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (allowed.has(id)) next.add(id);
      });
      return next;
    });
  }, [candidates]);

  const topics = useMemo(() => {
    const set = new Set<string>();
    candidates.forEach((c) => set.add(c.main_topic));
    return Array.from(set).sort();
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    return candidates;
  }, [candidates]);

  const displayedIds = useMemo(() => filteredCandidates.map((c) => c.id), [filteredCandidates]);
  const isAllDisplayedSelected = useMemo(() => {
    return displayedIds.length > 0 && displayedIds.every((id) => selectedIds.has(id));
  }, [displayedIds, selectedIds]);
  const isSomeDisplayedSelected = useMemo(() => {
    return displayedIds.some((id) => selectedIds.has(id));
  }, [displayedIds, selectedIds]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = !isAllDisplayedSelected && isSomeDisplayedSelected;
  }, [isAllDisplayedSelected, isSomeDisplayedSelected]);

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAllDisplayed = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const shouldSelectAll = !isAllDisplayedSelected;
      displayedIds.forEach((id) => {
        if (shouldSelectAll) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  // Unused function - kept for potential future use
  // const runWithConcurrency = async <T,>(
  //   items: T[],
  //   limit: number,
  //   fn: (item: T) => Promise<void>
  // ) => {
  //   let index = 0;
  //   const workerCount = Math.min(limit, items.length);
  //   const workers = Array.from({ length: workerCount }, async () => {
  //     // eslint-disable-next-line no-constant-condition
  //     while (true) {
  //       const i = index++;
  //       if (i >= items.length) return;
  //       await fn(items[i]);
  //     }
  //   });
  //   await Promise.all(workers);
  // };

  const handleBulkUpdate = async (action: 'approve' | 'reject') => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      setError(null);
      setBulkAction(action);
      const res = await axios.post<{
        success: boolean;
        data: { okCount: number; failCount: number; results: Array<{ id: string; ok: boolean; message?: string }> };
        message?: string;
      }>(`/api/admin/candidate-questions/bulk`, { action, ids });

      if (!res.data.success) {
        throw new Error(res.data.message || 'Bulk operation failed');
      }

      const okCount = res.data.data?.okCount || 0;
      const failures = (res.data.data?.results || []).filter((r) => !r.ok);
      
      if (action === 'approve' && okCount > 0) {
        if (failures.length === 0) {
          showSuccess(
            `${okCount} question${okCount === 1 ? '' : 's'} approved and promoted successfully.`,
            3000
          );
        } else {
          showSuccess(
            `${okCount} question${okCount === 1 ? '' : 's'} approved successfully.`,
            3000
          );
        }
      }
      
      if (failures.length > 0) {
        const errorMsg = `Bulk ${action} partially failed for ${failures.length} candidate(s): ` +
          failures.slice(0, 3).map((f) => `${f.id}${f.message ? ` (${f.message})` : ''}`).join(', ') +
          (failures.length > 3 ? '…' : '');
        setError(errorMsg);
        if (action === 'approve') {
          showError(errorMsg, 4500);
        }
      }
      setSelectedIds(new Set());
      await loadCandidates();
    } catch (err: any) {
      console.error(`[AdminQuestionReview] Failed bulk ${action}`, err);
      const errorMsg = err?.response?.data?.message ||
        `Failed to ${action} one or more candidate questions. See console for details.`;
      setError(errorMsg);
      if (action === 'approve') {
        showError(errorMsg, 4500);
      }
    } finally {
      setBulkAction(null);
    }
  };

  const handleApprove = async (candidate: CandidateQuestion) => {
    try {
      setUpdatingId(candidate.id);
      await axios.post(`/api/admin/candidate-questions/${candidate.id}/approve`);
      showSuccess('Question approved and promoted successfully.', 3000);
      await loadCandidates();
    } catch (err: any) {
      console.error('[AdminQuestionReview] Failed to approve candidate', err);
      const errorMsg = err?.response?.data?.message ||
        'Failed to approve candidate question. See console for details.';
      setError(errorMsg);
      showError(errorMsg, 4500);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleReject = async (candidate: CandidateQuestion) => {
    const confirmed = window.confirm(
      'Reject this question? It will remain in the staging table but marked as rejected.'
    );
    if (!confirmed) return;

    try {
      setUpdatingId(candidate.id);
      await axios.post(`/api/admin/candidate-questions/${candidate.id}/reject`);
      await loadCandidates();
    } catch (err: any) {
      console.error('[AdminQuestionReview] Failed to reject candidate', err);
      setError(
        err?.response?.data?.message ||
          'Failed to reject candidate question. See console for details.'
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="admin-page">
      <SEOHead
        title="Admin | Question Review"
        description="Review and approve AI-generated candidate questions."
      />
      <header className="admin-page__header">
        <h1 className="admin-page__title">Question Review</h1>
        <p className="admin-page__subtitle">
          Review AI-generated and editor-submitted questions before they go live.
          Approve high-quality questions or reject candidates.
            </p>
      </header>
      <div className="admin-page__filters">
            <label>
              Topic:
              <select
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                style={{ marginLeft: 'var(--space-2)', padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}
              >
                <option value="">All main topics</option>
                {topics.map((topic) => (
                  <option key={topic} value={topic}>{topic}</option>
                ))}
              </select>
            </label>
            <label className="admin-checkbox">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={isAllDisplayedSelected}
                onChange={toggleSelectAllDisplayed}
                disabled={loading || !!bulkAction}
              />
              Select all
            </label>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => loadCandidates(true)}
              disabled={loading || refreshing}
            >
              <svg
                className={refreshing ? 'spinning' : ''}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 1.33333V4.66667M8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 9.76811 2.03571 11.4638 3.28596 12.714M8 1.33333L11.3333 4.66667M8 11.3333V14.6667M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 6.23189 13.9643 4.53619 12.714 3.28596M8 14.6667L4.66667 11.3333M11.3333 4.66667L12.714 3.28596M4.66667 11.3333L3.28596 12.714"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
      </div>

        <div className="admin-tools" style={{ marginBottom: 'var(--space-4)' }}>
            <div className="admin-tools__list" style={{ alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                className="btn btn-primary btn-compact"
                disabled={selectedIds.size === 0 || !!bulkAction || loading}
                onClick={() => handleBulkUpdate('approve')}
                title="Approve and promote all selected questions"
              >
                {bulkAction === 'approve' ? 'Approving…' : 'Approve selected'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                disabled={selectedIds.size === 0 || !!bulkAction || loading}
                onClick={() => handleBulkUpdate('reject')}
                title="Reject all selected questions"
              >
                {bulkAction === 'reject' ? 'Rejecting…' : 'Reject selected'}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-compact"
                disabled={selectedIds.size === 0 || !!bulkAction || loading}
                onClick={() => setSelectedIds(new Set())}
              >
                Clear selection
              </button>
            </div>
          </div>

        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : error ? (
          <div className="admin-error"><strong>Error:</strong> {error}</div>
        ) : filteredCandidates.length === 0 ? (
          <p className="admin-loading">No candidates match the filters.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {filteredCandidates.map((c) => (
              <article key={c.id} className="admin-card">
                <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                  <label className="admin-checkbox" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={(e) => toggleSelected(c.id, e.target.checked)}
                      disabled={loading || !!bulkAction}
                      aria-label={`Select ${c.id}`}
                    />
                  </label>
                  <div>
                    <span className={`admin-badge admin-badge--${c.difficulty}`}>
                      {c.difficulty === 'hard'
                        ? 'Hard (university)'
                        : c.difficulty === 'medium'
                          ? 'Medium (high school)'
                          : 'Easy'}
                    </span>
                    <span style={{ marginLeft: 'var(--space-2)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                      {c.main_topic} • {c.subtopic}
                    </span>
                  </div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)', fontWeight: 'var(--font-weight-medium)' }}>
                  {c.question_text}
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <table className="admin-table" style={{ marginBottom: 'var(--space-2)' }}>
                    <tbody>
                      <tr><td style={{ width: 24 }}>A.</td><td>{c.option_a}</td></tr>
                      <tr><td>B.</td><td>{c.option_b}</td></tr>
                      <tr><td>C.</td><td>{c.option_c}</td></tr>
                      <tr><td>D.</td><td>{c.option_d}</td></tr>
                    </tbody>
                  </table>
                  <div style={{ fontSize: 'var(--font-size-sm)' }}>
                    Correct: <strong>{c.correct_answer}</strong>
                  </div>
                </div>
                {duplicateData[c.id]?.hasDuplicates && (
                  <div className="admin-callout" style={{ marginBottom: 'var(--space-3)' }}>
                    <details>
                      <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        {duplicateData[c.id].exactMatches.length > 0 
                          ? `⚠️ ${duplicateData[c.id].exactMatches.length} Exact Duplicate(s) Found`
                          : `⚠️ ${duplicateData[c.id].similarMatches.length} Similar Match(es) Found`}
                      </summary>
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                        {duplicateData[c.id].exactMatches.length > 0 && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Exact Matches:</strong>
                            {duplicateData[c.id].exactMatches.map((match, idx) => (
                              <div key={match.questionId} style={{ marginTop: '0.25rem', fontSize: '0.9em' }}>
                                <div><strong>ID:</strong> {match.questionId}</div>
                                <div><strong>Question:</strong> {match.questionText}</div>
                                <div><strong>Category:</strong> {match.category} | <strong>Difficulty:</strong> {match.difficulty}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {duplicateData[c.id].similarMatches.length > 0 && (
                          <div>
                            <strong>Similar Matches:</strong>
                            {duplicateData[c.id].similarMatches.slice(0, 3).map((match, idx) => (
                              <div key={match.questionId} style={{ marginTop: '0.25rem', fontSize: '0.9em' }}>
                                <div><strong>ID:</strong> {match.questionId} <span style={{ color: '#666' }}>({Math.round(match.similarityScore * 100)}% similar)</span></div>
                                <div><strong>Question:</strong> {match.questionText}</div>
                                <div><strong>Category:</strong> {match.category} | <strong>Difficulty:</strong> {match.difficulty}</div>
                              </div>
                            ))}
                            {duplicateData[c.id].similarMatches.length > 3 && (
                              <div style={{ marginTop: '0.25rem', fontSize: '0.9em', color: '#666' }}>
                                ...and {duplicateData[c.id].similarMatches.length - 3} more
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </details>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-compact"
                    disabled={
                      !!bulkAction || updatingId === c.id || c.review_status === 'approved'
                    }
                    onClick={() => handleApprove(c)}
                  >
                    {updatingId === c.id
                      ? 'Approving...'
                      : 'Approve & promote'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-compact"
                    disabled={!!bulkAction || updatingId === c.id}
                    onClick={() => handleReject(c)}
                  >
                    {updatingId === c.id
                      ? 'Rejecting...'
                      : 'Reject'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
    </div>
  );
};

export default AdminQuestionReview;



